import { CarouselItemVariants } from 'constants/carousel';
import clsx from 'clsx';
import { Fragment, useEffect, useRef, useState } from 'react';
import MultiCarousel from 'react-multi-carousel';
import Spinner from 'components/Qds/Spinner';
import Icon from 'components/ui/Icon';
import CustomSliderArrow from 'components/ui/CustomSliderArrow';
import tailwind from 'utils/breakpoints';
import { useVerticalSwipePreventer } from 'hooks/useVerticalSwipePreventer';
import { findDescription } from '../Utilities';
import CarouselFilter from '../CarouselFilter';
import styles from '../Carousel.module.scss';
import type { Carousel, Slide } from 'types/Utilities';

type Props = Carousel & {
  originalSlides: Slide[];
};

const Default = ({
  counter = true,
  expand,
  isMobile,
  itemVariant,
  onClose,
  originalSlides = [],
  resolveItemComponent,
  slides = [],
  startSlide = 0,
  thumbnails = true,
  updateResultsByTreatment,
}: Props): JSX.Element => {
  const sliderRef = useRef<MultiCarousel | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [activeSlide, setActiveSlide] = useState(startSlide);
  const [blockBeforeChange, setBlockBeforeChange] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [description, setDescription] = useState('');
  const [currentFilter, setCurrentFilter] = useState('All');
  useVerticalSwipePreventer(containerRef);

  useEffect(() => {
    if (sliderRef && sliderRef.current) {
      sliderRef.current.goToSlide(activeSlide);
    }

    updateDescription(activeSlide);
    setBlockBeforeChange(false);
  }, [expand]);

  const onThumbnailClick = (i: number): void => {
    setActiveSlide(i);
    if (sliderRef && sliderRef.current) {
      sliderRef.current.goToSlide(i);
    }
  };

  const updateDescription = (i = 0): void => {
    setDescription(findDescription(slides[i]));
  };

  const nextSlide = (): void => {
    if (activeSlide < slides.length - 1) {
      setActiveSlide(activeSlide + 1);
      if (sliderRef && sliderRef.current) {
        sliderRef.current.goToSlide(activeSlide + 1);
      }
    }
  };

  const prevSlide = (): void => {
    if (activeSlide > 0) {
      setActiveSlide(activeSlide - 1);
      if (sliderRef && sliderRef.current) {
        sliderRef.current.goToSlide(activeSlide - 1);
      }
    }
  };

  const onFilterChange = (value: string): void => {
    if (sliderRef && sliderRef.current && currentFilter !== value) {
      sliderRef.current.goToSlide(0);
      setActiveSlide(0);
    }

    setCurrentFilter(value);
  };

  return (
    <div className="fixed top-0 left-0 pt-4 md:px-10 w-full h-screen z-50 pointer-events-auto bg-darkGrey">
      <div
        className={clsx('fixed h-full w-full z-9', !isLoading && 'hidden')}
        style={{ top: '50%', left: '50%' }}
      >
        <Spinner size={24} />
      </div>
      <div className="flex flex-col h-full md:w-11/12 lg:max-w-screen-xl mx-auto">
        <div className="grid grid-cols-3 pb-4">
          <div className="flex items-center justify-center w-full col-start-2 px-4 md:px-0">
            {counter && (
              <p className="text-white text-center col-start-2 flex h-full justify-center items-center">
                <strong>
                  {activeSlide + 1}/{slides.length}
                </strong>
              </p>
            )}
          </div>
          <div className="w-full px-4 md:px-0">
            <div className="flex items-center justify-end">
              {itemVariant === CarouselItemVariants.BEFORE_AFTER && (
                <CarouselFilter
                  slides={originalSlides}
                  isGrid={false}
                  updateResultsByTreatment={updateResultsByTreatment}
                  onFilterChange={onFilterChange}
                />
              )}
              <div
                className="p-3 bg-white rounded-full hover:bg-bluishGrey cursor-pointer"
                onClick={() => {
                  onClose?.();
                }}
              >
                <Icon
                  className="w-4"
                  type="Xmark"
                  color={tailwind.theme.backgroundColor.tertiary}
                />
              </div>
            </div>
          </div>
        </div>
        <div
          className={clsx('relative', isLoading && 'invisible')}
          ref={containerRef}
        >
          <MultiCarousel
            ref={sliderRef}
            responsive={{
              all: {
                breakpoint: { max: 4000, min: 0 },
                items: 1,
              },
            }}
            customLeftArrow={
              <CustomSliderArrow
                className={clsx(
                  styles.carouselArrowLeft,
                  'hidden md:block w-10 h-10 rounded-full border-white border-2 p-2 bottom-0 md:bottom-auto m-4 md:m-auto bg-white md:bg-transparent',
                )}
                direction="left"
              />
            }
            customRightArrow={
              <CustomSliderArrow
                className={clsx(
                  styles.carouselArrowRight,
                  'hidden md:block w-10 h-10 rounded-full border-white border-2 p-2 bottom-0 md:bottom-auto m-4 md:m-auto bg-white md:bg-transparent',
                )}
                direction="right"
              />
            }
            containerClass="flex relative items-center overflow-hidden h-full"
            sliderClass={clsx(
              'flex relative flex-row list-none p-0 m-0',
              styles['carousel-track'],
            )}
            itemClass="shrink-0 self-center"
            afterChange={() => {
              if (isLoading) {
                setIsLoading(false);
              }
            }}
            beforeChange={(nextSlide, { currentSlide }) => {
              if (!blockBeforeChange) {
                let index;
                if (Math.abs(nextSlide + currentSlide) !== 1) {
                  index = nextSlide;
                } else if (nextSlide > currentSlide) {
                  index = activeSlide + 1;
                } else if (nextSlide === 0) {
                  index = 0;
                } else {
                  index = activeSlide - 1;
                }

                setActiveSlide(index);
                updateDescription(index);
              }
            }}
          >
            {slides.map((slide, i) => (
              <Fragment key={i}>{resolveItemComponent(slide)}</Fragment>
            ))}
          </MultiCarousel>
        </div>
        <p
          className="text-white text-center mb-20 pt-1 hidden md:block"
          dangerouslySetInnerHTML={{
            __html: description,
          }}
        />
        {thumbnails && !isMobile ? (
          <div className="mb-6 hidden md:block">
            <MultiCarousel
              responsive={{
                all: {
                  breakpoint: { max: 4000, min: 0 },
                  items: 10,
                },
              }}
              customLeftArrow={
                <CustomSliderArrow
                  className={clsx(
                    styles.carouselArrowLeft,
                    'w-10 top-0 h-full px-2 bg-darkGrey',
                  )}
                  direction="left"
                  color="#fff"
                />
              }
              customRightArrow={
                <CustomSliderArrow
                  className={clsx(
                    styles.carouselArrowRight,
                    'w-10 top-0 h-full px-2 bg-darkGrey',
                  )}
                  direction="right"
                  color="#fff"
                />
              }
              containerClass={clsx(
                'flex relative items-center overflow-hidden',
                slides.length <= 8 && 'justify-center',
              )}
              sliderClass="flex relative flex-row list-none p-0 m-0"
              itemClass="shrink-0 self-center"
            >
              {slides.map((slide, i) => (
                <div
                  key={i}
                  className="mx-3 rounded-md overflow-hidden cursor-pointer no-inline-block-image"
                  onClick={() => {
                    onThumbnailClick(i);
                  }}
                >
                  {resolveItemComponent(slide, true)}
                </div>
              ))}
            </MultiCarousel>
          </div>
        ) : null}

        <div
          className="w-full bottom-0 fixed h-16 md:hidden"
          style={{ zIndex: 99 }}
        >
          <div
            className={clsx(
              'absolute z-40 w-1/2 md:w-auto cursor-pointer h-16',
              activeSlide === 0 && 'hidden',
            )}
            onClick={prevSlide}
          >
            <CustomSliderArrow
              className={clsx(
                styles.carouselArrowLeft,
                'w-10 h-10 rounded-full border-white border-2 p-2 bottom-0 md:bottom-auto m-4 md:m-auto bg-white md:bg-transparent',
              )}
              direction="left"
            />
          </div>
          <div
            className={clsx(
              'absolute z-40 w-1/2 md:w-auto cursor-pointer h-16 right-0',
              activeSlide === slides.length - 1 && 'hidden',
            )}
            onClick={nextSlide}
          >
            <CustomSliderArrow
              className={clsx(
                styles.carouselArrowRight,
                'w-10 h-10 rounded-full border-white border-2 p-2 bottom-0 md:bottom-auto m-4 md:m-auto bg-white md:bg-transparent',
              )}
              direction="right"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Default;
